import { useSelector } from 'react-redux';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Offcanvas } from 'react-bootstrap';
import xss from 'xss';
import Link from 'components/Link';
import { DISCOUNT_ROUTE_MAP } from 'constants/discount';
import isEmpty from 'utils/is-empty';
import styles from './DiscountDrawer.module.scss';

function getThresholdInfo({ lineItems = [], thresholdInfos = [] }) {
  let minRemaining = 99;

  for (let i = thresholdInfos.length - 1; i >= 0; i--) {
    const { targetProducts, totalDiscountThreshold } = thresholdInfos[i];

    if (totalDiscountThreshold === 0 || targetProducts.length === 0) {
      return '';
    }
    const targetProductsSet = new Set(targetProducts.map(({ sku }) => sku));

    let eligibleQuantity = 0;
    const numberOfLineItems = lineItems.length;
    for (let i = 0; i < numberOfLineItems; i++) {
      const { SKU, quantity } = lineItems[i];
      if (targetProductsSet.has(SKU)) {
        eligibleQuantity += quantity;
      }
    }
    const remainingQuantity = totalDiscountThreshold - eligibleQuantity;
    if (remainingQuantity < minRemaining) minRemaining = remainingQuantity;
  }

  return minRemaining > 0
    ? `Add ${minRemaining} more qualifying product${
        minRemaining === 1 ? '' : 's'
      } to reach discount`
    : '';
}

function transformDiscountData({ discountData, groupedDiscountsContent }) {
  const nonGroupedDiscounts = [];
  const groupedDiscountsMap = {};

  for (let i = 0; i < (discountData.length ?? 0); i++) {
    const currentDiscount = discountData[i];
    const {
      description,
      discountType,
      groupKey = '',
      id,
      isActive,
      onlineNameOverride,
      slug,
      targetProducts = [],
      totalDiscountThreshold,
    } = currentDiscount;

    if (groupKey) {
      if (groupedDiscountsMap[groupKey]) {
        groupedDiscountsMap[groupKey].thresholdInfos.push({
          targetProducts,
          totalDiscountThreshold,
        });
      } else {
        const groupedContent =
          groupedDiscountsContent?.find(
            (x) => x.discount_id.toLowerCase() === groupKey
          ) ?? {};

        groupedDiscountsMap[groupKey] = {
          description: groupedContent?.description || description,
          discountType,
          groupKey,
          id,
          isActive,
          onlineNameOverride: groupedContent?.online_name || onlineNameOverride,
          slug,
          thresholdInfos: [
            {
              targetProducts,
              totalDiscountThreshold,
            },
          ],
        };
      }
    } else {
      nonGroupedDiscounts.push({
        ...currentDiscount,
        thresholdInfos: [
          {
            targetProducts,
            totalDiscountThreshold,
          },
        ],
      });
    }
  }

  const drawerData = [
    ...nonGroupedDiscounts,
    ...Object.values(groupedDiscountsMap),
  ];

  return drawerData;
}

export default function DiscountDrawer({
  showDrawer = false,
  discountData = [],
  groupedDiscountsContent,
  toggleDrawer,
}) {
  const { lineItems } = useSelector(({ cart }) => cart);
  const cartInitialized = useSelector((state) => !isEmpty(state.cart.scope));

  const drawerData = transformDiscountData({
    discountData,
    groupedDiscountsContent,
  });

  const handleDrawerClose = () => {
    if (toggleDrawer && typeof toggleDrawer === 'function') {
      toggleDrawer();
    }
  };

  return (
    <Offcanvas
      show={showDrawer}
      aria-label="Available Discounts Drawer"
      className={styles.menu_offcanvas}
      id="available_discounts_drawer"
      name="available_discounts_drawer"
      placement="end"
      onHide={handleDrawerClose}
    >
      <div className="d-flex flex-column h-100 w-100 text-primary">
        <div className="h-100 d-flex flex-column">
          <div className="d-flex flex-row align-items-right justify-content-end w-100 p-5">
            <button
              className="border-0 bg-transparent font-sofia px-0"
              id="menu_drawer_close"
              title="Menu Close"
              type="button"
              onClick={handleDrawerClose}
            >
              <span>
                <FontAwesomeIcon className={styles.icon} icon={faXmark} />
              </span>
            </button>
          </div>
          <div className="py-4 px-8 px-md-15 h-100 overflow-auto">
            <h1 className="pb-8 pb-md-14">Additional Details</h1>
            <ul className="list-unstyled">
              {drawerData?.map((data) => {
                const {
                  description,
                  discountType,
                  groupKey,
                  id,
                  isActive,
                  onlineName,
                  onlineNameOverride,
                  slug,
                  thresholdInfos,
                } = data;

                const thresholdInfo = getThresholdInfo({
                  lineItems,
                  thresholdInfos,
                });

                const baseUrl = `/specials/${
                  DISCOUNT_ROUTE_MAP[discountType] || discountType
                }`;

                const url = `${baseUrl}/${
                  groupKey || id
                }?filters=${encodeURIComponent(
                  `Special^term^${groupKey || slug}`
                )}`;

                return (
                  <li key={id} className="border-bottom my-8">
                    <Link
                      id="discount-drawer"
                      href={url}
                      data-value={`lume-special-${id}`}
                      aria-label={`Lume Special ${onlineName}`}
                      className={
                        isActive ? styles.header_link : styles.inactive_link
                      }
                    >
                      <h3
                        className={classNames(
                          'fs-9 font-sofia text-primary fw-700 text-uppercase',
                          { 'pb-8': !thresholdInfo }
                        )}
                      >
                        {onlineNameOverride === ''
                          ? onlineName
                          : onlineNameOverride}
                      </h3>
                    </Link>
                    {cartInitialized && thresholdInfo && (
                      <Link
                        href={url}
                        data-value={`lume-special-${id}`}
                        aria-label={`Lume Special ${onlineName}`}
                      >
                        <span
                          className={classNames(
                            'fs-6 fs-md-7 fw-600 px-2 py-2',
                            styles.threshold_info,
                            { 'mb-4': !description }
                          )}
                        >
                          {thresholdInfo}
                        </span>
                      </Link>
                    )}
                    {description && (
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: xss(description) }}
                        className="fs-7 fs-lg-8 text-primary my-4 pb-8"
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </Offcanvas>
  );
}
